import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { formatMoney } from "../../../../features/utils/formatMoney";
import {
  formatChangeDate,
  getChangeSelectedOfferFareHint,
  getRequestedDateChangeLines,
  parseChangeSelectedOffer,
} from "../../../../data/flight/order/getRequestedDateChange";

function legLabel(leg) {
  const from = leg?.departure?.location || leg?.departureLocation || "";
  const to = leg?.arrival?.location || leg?.arrivalLocation || "";
  const date = formatChangeDate(
    leg?.departure?.date || leg?.departureDate || "",
  );
  const time = leg?.departure?.time || leg?.departureTime || "";
  const flight =
    [leg?.airline?.marketing || leg?.airline, leg?.flightNumber]
      .filter(Boolean)
      .join(" ") || "";
  return {
    route: [from, to].filter(Boolean).join(" → "),
    when: [date, time].filter(Boolean).join(" · "),
    flight,
  };
}

export default function ChangeSelectedOfferModal({ open, setOpen, data }) {
  const offer = useMemo(
    () => (open ? parseChangeSelectedOffer(data) : null),
    [open, data],
  );
  const hint = useMemo(
    () => (open ? getChangeSelectedOfferFareHint(data) : null),
    [open, data],
  );
  const lines = useMemo(
    () => (open ? getRequestedDateChangeLines(data) : []),
    [open, data],
  );

  if (!open || !offer) return null;

  const money = (amount) => (amount == null ? "—" : formatMoney(amount) || "—");
  const byPaxType = Array.isArray(hint?.byPaxType) ? hint.byPaxType : [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/40"
        onClick={() => setOpen?.(false)}
      />
      <div
        className="relative w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-900 leading-6">
              Selected offer
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              What the agent picked, compared with the current ticket.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen?.(false)}
            className="mt-0.5 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <Icon icon="material-symbols:close" className="text-xl" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-4 space-y-4">
          {hint ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
              <div className="text-sm font-semibold text-amber-900">
                Fare difference
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div className="rounded-lg bg-white/70 px-2.5 py-2">
                  <div className="text-[11px] text-amber-800">
                    Current ticket
                  </div>
                  <div className="font-semibold text-amber-950">
                    {money(hint.oldTotal)}
                  </div>
                </div>
                <div className="rounded-lg bg-white/70 px-2.5 py-2">
                  <div className="text-[11px] text-amber-800">
                    Selected offer
                  </div>
                  <div className="font-semibold text-amber-950">
                    {money(hint.offerTotal)}
                  </div>
                </div>
                <div className="rounded-lg bg-white/70 px-2.5 py-2">
                  <div className="text-[11px] text-amber-800">Difference</div>
                  <div className="font-semibold text-amber-950">
                    {money(hint.difference)}
                  </div>
                </div>
              </div>

              {byPaxType.length ? (
                <div className="mt-3 rounded-lg border border-amber-200/80 bg-white/60 overflow-hidden">
                  <div className="px-2.5 py-1.5 text-[11px] font-semibold text-amber-900">
                    By passenger type
                  </div>
                  <div className="divide-y divide-amber-100">
                    {byPaxType.map((row) => (
                      <div
                        key={row.type}
                        className="px-2.5 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm"
                      >
                        <div className="font-medium text-amber-950">
                          {row.label}
                          {row.count ? (
                            <span className="ml-1 text-[11px] font-normal text-amber-800">
                              × {row.count}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-amber-900/90 sm:text-right">
                          <span>{money(row.oldTotal)}</span>
                          <span className="mx-1 text-amber-700">→</span>
                          <span>{money(row.offerTotal)}</span>
                          <span className="ml-2 font-semibold text-amber-950">
                            diff {money(row.difference)}
                          </span>
                          {row.perPassenger != null ? (
                            <span className="ml-1.5 text-[11px] text-amber-800">
                              ({money(row.perPassenger)} / pax)
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <p className="mt-2 text-xs text-amber-800">
                Guide only — confirm on the airline before you lock the quote.
              </p>
            </div>
          ) : null}

          {lines.length ? (
            <div className="rounded-xl border border-[#E8EEF5] overflow-hidden">
              <div className="px-3 py-2 bg-[#F4F7FC] text-sm font-semibold text-gray-900">
                From this, to this
              </div>
              <div className="divide-y divide-[#E8EEF5]">
                {lines.map((line) => (
                  <div
                    key={`${line.segment}-${line.route}`}
                    className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm"
                  >
                    <span className="font-medium text-gray-900">
                      {line.routeChanged
                        ? `${line.originalRoute} → ${line.route}`
                        : line.route}
                    </span>
                    <span className="text-gray-600">
                      {line.oldLabel} → {line.newLabel}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-[#E8EEF5] overflow-hidden">
            <div className="px-3 py-2 bg-[#EAF1FE] text-sm font-semibold text-[#0B49B0]">
              New itinerary on the offer
            </div>
            <div className="divide-y divide-[#E8EEF5]">
              {(offer?.directions || []).map((direction, dirIndex) => {
                const legs = Array.isArray(direction) ? direction : [];
                const first = legs[0];
                const last = legs[legs.length - 1];
                const heading = [
                  first?.departure?.location,
                  last?.arrival?.location,
                ]
                  .filter(Boolean)
                  .join(" → ");
                return (
                  <div key={dirIndex} className="px-3 py-3 space-y-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {heading || `Direction ${dirIndex + 1}`}
                      {legs.length > 1 ? (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {legs.length - 1} stop
                          {legs.length - 1 === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                    {legs.map((leg, legIndex) => {
                      const row = legLabel(leg);
                      return (
                        <div
                          key={leg?.segmentRef || `${dirIndex}-${legIndex}`}
                          className="rounded-lg bg-[#F8FAFC] px-2.5 py-2 text-sm"
                        >
                          <div className="font-medium text-gray-900">
                            {row.route || "—"}
                          </div>
                          <div className="text-gray-600">
                            {[row.when, row.flight]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
